/**
 * Route-follow arrival sequencer. While a route is activated for follow, this
 * watches own ship against the active leg and, the moment we reach the leg's
 * waypoint, rolls the active leg on to the next one, or ends the follow with a
 * "route complete" when that was the last waypoint.
 *
 * "Reached" is the pure advanceDecision: inside the waypoint's arrival radius, or
 * past its abeam line if we cleared it wide, so a waypoint passed without a clean
 * hit still sequences rather than leaving us stuck steering a leg we've already
 * gone by. The audible arrival ALARM for an armed waypoint is raised elsewhere
 * (evaluateRouteAlarms → the live alarm state → the siren and banner); here we
 * give the always-on arrival notification (a toast and a confirm buzz) so an
 * unarmed waypoint still tells us it's done, and we drive the leg advance.
 *
 * The advance is edge-triggered: a ref remembers the leg whose arrival we've
 * already handled, so we advance once as we reach a waypoint rather than every
 * fix we sit inside its radius. It clears when we stop following, so re-activating
 * the same route re-arms cleanly.
 */
import { useEffect, useRef } from 'react';
import { useChartPlotterStore } from '@/store';
import { useRoutingStore } from '@/store/routing';
import { useShellStore } from '@/store/shell';
import {
  advanceDecision,
  approachArrived,
  clampLegIndex,
  APPROACH_LEG_INDEX,
  type Route,
} from '@/routing';
import { haptic } from '@/ui/haptics';

/**
 * The name of the waypoint we are making for on a leg: its label, or its 1-based
 * number. On the approach leg that is WP1 itself rather than the far end of a leg.
 */
function legTargetName(route: Route, legIndex: number): string {
  if (legIndex === APPROACH_LEG_INDEX) {
    return route.waypoints[0]?.name?.trim() || 'WP1';
  }
  const tw = route.waypoints[legIndex + 1];
  return tw?.name?.trim() || `WP${legIndex + 2}`;
}

export function RouteFollowRunner() {
  const position = useChartPlotterStore((s) => s.ownship.position);
  const followRouteId = useRoutingStore((s) => s.followRouteId);
  const activeLegIndex = useRoutingStore((s) => s.activeLegIndex);
  const routes = useRoutingStore((s) => s.routes);
  const handled = useRef<{ routeId: string; leg: number } | null>(null);

  useEffect(() => {
    // Not following (or no fix yet): nothing to sequence. Clear the edge guard
    // when the follow is off so the next activation starts fresh.
    if (!followRouteId || activeLegIndex == null || !position) {
      if (!followRouteId) handled.current = null;
      return;
    }
    const route = routes.find((r) => r.id === followRouteId);
    if (!route || route.waypoints.length < 2) return;

    // On the approach we're steering to WP1, so arrival is reaching WP1 and the
    // next leg is leg 0, the leg out of it. Everywhere else the usual decision.
    const onApproach = activeLegIndex === APPROACH_LEG_INDEX;
    const idx = onApproach ? APPROACH_LEG_INDEX : clampLegIndex(route, activeLegIndex);
    const dec = onApproach
      ? { arrived: approachArrived(route, position), complete: false, nextLegIndex: 0 }
      : advanceDecision(route, position, idx);
    if (!dec.arrived) return;

    // Only act once for this leg's arrival.
    if (handled.current && handled.current.routeId === followRouteId && handled.current.leg === idx) {
      return;
    }
    handled.current = { routeId: followRouteId, leg: idx };

    const name = legTargetName(route, idx);
    const rs = useRoutingStore.getState();
    const showToast = useShellStore.getState().showToast;
    haptic('commit');
    if (dec.complete) {
      // Reached the final waypoint: follow ends here.
      rs.deactivateRoute();
      showToast(`Arrived ${name} — route complete`);
    } else {
      rs.advanceActiveLeg();
      showToast(`Arrived ${name} — steering to the next waypoint`);
    }
  }, [followRouteId, activeLegIndex, position, routes]);

  return null;
}
